import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Button,
  Checkbox,
  SelectChangeEvent,
  TableCell,
  TableRow,
  useTheme,
} from '@mui/material'

import { FormSelect } from 'src/components/form-select'
import { TableRelationIcon } from 'src/components/table-relation-icon'
import { useAccountChangeUserRole, useMe, useResendAccountUserInvite } from 'src/hooks'

import { EAccountRoles, EAccountStatuses, IAccountUserResponseDto } from 'src/types'
import { getTableTimestamp, joinAbsRoute } from 'src/common/utils'
import { AppEvents, ROUTES } from 'src/constants'

import { hasResendExpired } from '../utils'
import { useStyles } from '../styles'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { ScannerStepsConnection } from 'src/components/scanner-steps-connection'

interface ITableBodyRowProps {
  row: IAccountUserResponseDto
  handleItemCheck: Function
  isItemSelected: (id: string) => boolean
}

const selectItems = Object.entries(EAccountRoles).map(([label, value]) => ({ label, value }))

const TableBodyRow = ({ row, handleItemCheck, isItemSelected }: ITableBodyRowProps) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { accountId } = useParams()
  const [role, setRole] = useState(row.roles[0])
  const { mutateAsync, isLoading } = useAccountChangeUserRole()
  const { data: me } = useMe()
  const classes = useStyles()
  const theme = useTheme()
  const { mutate: resendInvite, isLoading: isResending } = useResendAccountUserInvite()

  const handleRoleChange = async (e: SelectChangeEvent) => {
    const role = e.target.value as EAccountRoles

    if (!me || me.id === row.user.id) {
      const detail = { message: t('users-change-role-warning.v') }

      const event = new CustomEvent(AppEvents.HTTP_ERROR, { detail })
      return document.dispatchEvent(event)
    }

    const resp = await mutateAsync({ roles: [role], id: row.user.id })
    if (!resp || role === EAccountRoles.owner) return

    setRole(role)
  }

  const handleResend = () => {
    resendInvite(row.user.id)
  }

  const handleStepClick = (id: string) => () =>
    navigate(joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.STEPS, id))

  const handleImpactClick = (id: string) => () =>
    navigate(joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.IMPACTS, id))

  const isTokenExpired = hasResendExpired(row.activation_token_sent_at)

  return (
    <TableRow key={row.user.id} className={classes.tableRow} sx={{ 'td, th': { border: 0 } }}>
      <TableCell align="center">
        <Checkbox
          checked={isItemSelected(row.id)}
          sx={{ color: theme.palette.custom.grey2 }}
          onChange={handleItemCheck(row.id)}
        />
      </TableCell>
      <TableCell>
        <Avatar
          alt={row.user.name ?? row.user.email}
          src={row.user.image_small || row.user.image}
          className={classes.tableIcon}
        />
      </TableCell>
      <TableCell data-status={row.status} className={classes.userStatus}>
        {row.status}
      </TableCell>
      <TableCell align="center">{row.user.email}</TableCell>
      <TableCell align="center">
        <FormSelect
          noClear
          value={role}
          handleChange={handleRoleChange}
          disabled={isLoading}
          items={selectItems}
        />
      </TableCell>
      <TableCell>
        <div className={classes.selectionWrapper}>
          {row.steps.map((step) => {
            return (
              <TableRelationIcon
                title={step.name}
                subtitle={`#${step.code}`}
                key={step.id}
                icon={step.image_small || step.image}
                iconAlt={step.name}
                handleClick={handleStepClick(step.id)}
              />
            )
          })}
          {row.roles[0] === EAccountRoles.scanner && (
            <ScannerStepsConnection selectedSteps={row.steps} scanerId={row.user.id} />
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className={classes.selectionWrapper}>
          {row.impacts.map((impact) => {
            return (
              <TableRelationIcon
                title={impact.name}
                subtitle={`#${impact.code}`}
                key={impact.id}
                icon={impact.image_small || impact.image}
                iconAlt={impact.name}
                handleClick={handleImpactClick(impact.id)}
              />
            )
          })}
        </div>
      </TableCell>
      <TableCell align="center" className={classes.userDate}>
        {getTableTimestamp({ date: row.created_at, timezone: me?.timezone })}
      </TableCell>
      <TableCell align="center" className={classes.userDate}>
        {getTableTimestamp({ date: row.updated_at, timezone: me?.timezone })}
      </TableCell>
      <TableCell align="center">
        <Button
          onClick={handleResend}
          disabled={row.status !== EAccountStatuses.pending || isResending || !isTokenExpired}
        >
          <HelperWrapper t="account-users-resend-button" />
        </Button>
      </TableCell>
    </TableRow>
  )
}

export { TableBodyRow }
